// ** Redux Imports
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"

// ** Services
import {
  addNewArrivalApi,
  updateNewArrivalApi,
  addOrUpdateProductApi,
  removeProductApi,
  deleteNewArrivalApi,
  getNewArrivalByUserApi,
  getVendorNewArrivalProductsApi,
  getAllNewArrivalAdminApi
} from "../../../../services/Web/newArrival.service"

import { toastSuccess, toastError } from "../../../../utility/toastutill"

//
// --------------------------------------------------
// GET COMPLETE ARRIVAL BY USER
// (Used for getting arrivalId + product list)
// --------------------------------------------------
export const getArrivalByUser = createAsyncThunk(
  "newArrival/getArrivalByUser",
  async (userId, { rejectWithValue }) => {
    try {
      const res = await getNewArrivalByUserApi(userId)
      return res.data.data || null
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      )
    }
  }
)

//
// --------------------------------------------------
// GET Vendor All New Arrival Products (merged list)
// --------------------------------------------------
export const getAllData = createAsyncThunk(
  "newArrival/getAllData",
  async (userId, { rejectWithValue }) => {
    try {
      const res = await getVendorNewArrivalProductsApi(userId)
      return res.data.data || []
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      )
    }
  }
)

//
// --------------------------------------------------
// ADMIN: Fetch All vendors’ NewArrival products
// --------------------------------------------------
export const getAdminNewArrivalData = createAsyncThunk(
  "newArrival/getAdminNewArrivalData",
  async (params, { rejectWithValue }) => {
    try {
      const res = await getAllNewArrivalAdminApi(params)
      return res.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      )
    }
  }
)

//
// --------------------------------------------------
// ADD NEW ARRIVAL
// --------------------------------------------------
export const addNewArrival = createAsyncThunk(
  "newArrival/addNewArrival",
  async ({ formData, userId }, { dispatch }) => {
    try {
      const res = await addNewArrivalApi(formData)
      if (res.data.success) {
        toastSuccess(res.data.message)
        await dispatch(getArrivalByUser(userId))
        await dispatch(getAllData(userId))
      }
      return res.data.success
    } catch (error) {
      toastError(error)
      return false
    }
  }
)

//
// --------------------------------------------------
// UPDATE NEW ARRIVAL
// --------------------------------------------------
export const updateNewArrival = createAsyncThunk(
  "newArrival/updateNewArrival",
  async ({ id, formData, userId }, { dispatch }) => {
    try {
      const res = await updateNewArrivalApi(id, formData)
      if (res.data.success) {
        toastSuccess(res.data.message)
        await dispatch(getArrivalByUser(userId))
        await dispatch(getAllData(userId))
      }
      return res.data.success
    } catch (error) {
      toastError(error)
      return false
    }
  }
)

//
// --------------------------------------------------
// ADD OR UPDATE PRODUCT
// --------------------------------------------------
export const addOrUpdateProduct = createAsyncThunk(
  "newArrival/addOrUpdateProduct",
  async ({ arrivalId, productData, userId }, { dispatch }) => {
    try {
      const res = await addOrUpdateProductApi(arrivalId, productData)
      if (res.data.success) {
        toastSuccess(res.data.message)
        await dispatch(getArrivalByUser(userId))
        await dispatch(getAllData(userId))
      }
      return res.data.success
    } catch (error) {
      toastError(error)
      return false
    }
  }
)

//
// --------------------------------------------------
// REMOVE PRODUCT
// --------------------------------------------------
export const removeProduct = createAsyncThunk(
  "newArrival/removeProduct",
  async ({ arrivalId, productData, userId }, { dispatch }) => {
    try {
      const res = await removeProductApi(arrivalId, productData)
      if (res.data.success) {
        toastSuccess(res.data.message)
        await dispatch(getArrivalByUser(userId))
        await dispatch(getAllData(userId))
      }
      return res.data.success
    } catch (error) {
      toastError(error)
      return false
    }
  }
)

//
// --------------------------------------------------
// DELETE NEW ARRIVAL
// --------------------------------------------------
export const deleteNewArrival = createAsyncThunk(
  "newArrival/deleteNewArrival",
  async ({ id, userId }, { dispatch }) => {
    try {
      const res = await deleteNewArrivalApi(id)
      if (res.data.success) {
        toastSuccess(res.data.message)
        await dispatch(getArrivalByUser(userId))
        await dispatch(getAllData(userId))
      }
      return id
    } catch (error) {
      toastError(error)
      return null
    }
  }
)

//
// Redux Slice
// --------------------------------------------------
export const newArrivalSlice = createSlice({
  name: "newArrival",
  initialState: {
    data: [], // Active product list for vendor
    arrival: null, // Full NewArrival object
    arrivalId: null,
    allData: [], // Flat merged products for vendor
    adminData: [], // All vendor products for admin
    adminPagination: { page: 1, limit: 10, total: 0, totalPages: 0 },
    loading: false,
    error: null
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getArrivalByUser.pending, (state) => {
        state.loading = true
      })
      .addCase(getArrivalByUser.fulfilled, (state, action) => {
        state.loading = false
        state.arrival = action.payload
        state.arrivalId = action.payload?._id || null
        state.data = action.payload?.products || []
      })
      .addCase(getArrivalByUser.rejected, (state, action) => {
        state.loading = false
        state.arrival = null
        state.arrivalId = null
        state.data = []
        state.error = action.payload
      })
      .addCase(getAllData.fulfilled, (state, action) => {
        state.allData = action.payload
      })
      .addCase(getAdminNewArrivalData.fulfilled, (state, action) => {
        state.adminData = action.payload?.data || []
        state.adminPagination = {
          page: action.payload?.page || 1,
          limit: action.payload?.limit || 10,
          total: action.payload?.total || 0,
          totalPages: action.payload?.totalPages || 0
        }
      })
  }
})

export default newArrivalSlice.reducer
